import { UserProfile } from 'fitbeat.gym-core';
import { BaseUserProfile, IFamilyPlanInvitation } from 'fitbeat.models';
import produce from 'immer';
import {
  ACCEPT_FAMILY_PLAN_INVITATION_FAILURE,
  ACCEPT_FAMILY_PLAN_INVITATION_REQUEST,
  ACCEPT_FAMILY_PLAN_INVITATION_SUCCESS,
  CANCEL_PENDING_FAMILY_PLAN_INVITATION_FAILURE,
  CANCEL_PENDING_FAMILY_PLAN_INVITATION_REQUEST,
  CANCEL_PENDING_FAMILY_PLAN_INVITATION_SUCCESS,
  CLOSE_INVITATION_CODE_WELCOME_SCREEN,
  INVITE_FAMILY_MEMBER_FAILURE,
  INVITE_FAMILY_MEMBER_REQUEST,
  INVITE_FAMILY_MEMBER_SUCCESS,
  LOAD_CONFIRM_REMOVE_SCREEN,
  LOAD_INVITATION_CODE_SIGNUP_SCREEN,
  LOAD_INVITE_MEMBER_SCREEN,
  MANAGE_MEMBERS,
  MANAGE_MEMBERS_DONE,
  PIN_ATTEMPT_CHANGED,
  REMOVE_FAMILY_PLAN_MEMBER_FAILURE,
  REMOVE_FAMILY_PLAN_MEMBER_REQUEST,
  REMOVE_FAMILY_PLAN_MEMBER_SUCCESS,
  SWITCH_MEMBER_SCREEN_LOADING_FAILURE,
  SWITCH_MEMBER_SCREEN_LOADING_REQUEST,
  SWITCH_MEMBER_SCREEN_LOADING_SUCCESS,
  SWOP_USER_FAILURE,
  SWOP_USER_REQUEST,
  SWOP_USER_SUCCESS,
  UPDATE_USER_PROFILE_COLOR_FAILURE,
  UPDATE_USER_PROFILE_COLOR_REQUEST,
  UPDATE_USER_PROFILE_COLOR_SUCCESS,
  UPDATE_USER_PROFILE_PIN_FAILURE,
  UPDATE_USER_PROFILE_PIN_REQUEST,
  UPDATE_USER_PROFILE_PIN_SUCCESS,
} from './actionTypes';

export interface ISwitchMemberUserProfile extends UserProfile {
  isCurrentLoggedInUser: boolean;
  isSwoppingToUser: boolean;
  initials: string;
}

export interface IPendingInvite extends IFamilyPlanInvitation {
  isCancellingInvite: boolean;
}

interface IFamilyMembersView {
  isSwitchMemberScreenLoading: boolean;
  isInviteMemberButtonLoading: boolean;
  userIsContinuingFromSignout: boolean;
  isAcceptingInvitation: boolean;
  successfullyJoinedFamilyPlan: boolean | undefined;
  joinedFamilyPlanInformation: string,
  canInviteMoreFamilyMembers: boolean;
  inviteFamilyMemberError: string | undefined;
  cancelPendingInviteError: string | undefined;
  isProfileColorChangeLoading: boolean;
  isLeavingFamilyPlan: boolean;
  removeFamilyPlanMemberError: string | undefined;
  isProfilePinChangeLoading: boolean;
  isManagingMembers: boolean;
  showSwitchingMemberFaliure: boolean;
  pinAttempt: string;
}

interface IFamilyMembersData {
  familyMembers: ISwitchMemberUserProfile[];
  pendingInvites: IPendingInvite[],
}

export interface IFamilyMembersState {
  view: IFamilyMembersView;
  data: IFamilyMembersData;
}

const initialState: IFamilyMembersState = {
  view: {
    isSwitchMemberScreenLoading: false,
    isInviteMemberButtonLoading: false,
    userIsContinuingFromSignout: false,
    isAcceptingInvitation: false,
    successfullyJoinedFamilyPlan: undefined,
    joinedFamilyPlanInformation: '',
    canInviteMoreFamilyMembers: false,
    inviteFamilyMemberError: undefined,
    cancelPendingInviteError: undefined,
    isProfileColorChangeLoading: false,
    isLeavingFamilyPlan: false,
    removeFamilyPlanMemberError: undefined,
    isProfilePinChangeLoading: false,
    isManagingMembers: false,
    showSwitchingMemberFaliure: false,
    pinAttempt: '',
  },
  data: {
    familyMembers: [],
    pendingInvites: [],
  },
};

export const familyMembersReducer = produce((draft = initialState, action: any): IFamilyMembersState => {
  switch (action.type) {
    case SWITCH_MEMBER_SCREEN_LOADING_REQUEST: {
      draft.view.isSwitchMemberScreenLoading = true;
      break;
    }

    case SWITCH_MEMBER_SCREEN_LOADING_SUCCESS: {
      const familyMembers: ISwitchMemberUserProfile[] = action.payload.familyMembers;
      const invitedUsers: IPendingInvite[] = action.payload.invitedUsers;
      const canInviteMoreFamilyMembers = action.payload.canInviteMoreFamilyMembers;

      const currentLoggedInUserId = action.payload.currentLoggedInUserId;

      familyMembers.map((member: ISwitchMemberUserProfile) => {
        member.isCurrentLoggedInUser = member.uid === currentLoggedInUserId;
        member.initials = BaseUserProfile.getUserInitials(member as UserProfile);
        return member;
      });

      const sortedFamilyMembers = sortFamilyMembers(familyMembers);

      draft.view.isSwitchMemberScreenLoading = false;
      draft.data.familyMembers = sortedFamilyMembers;
      draft.data.pendingInvites = invitedUsers;
      draft.view.canInviteMoreFamilyMembers = canInviteMoreFamilyMembers;
      draft.view.cancelPendingInviteError = undefined;
      draft.view.isManagingMembers = false;

      break;
    }

    case SWITCH_MEMBER_SCREEN_LOADING_FAILURE: {
      draft.view.isSwitchMemberScreenLoading = false;
      break;
    }

    case SWOP_USER_REQUEST: {
      setSwitchingToFamilyMember(draft, action.payload.swoppingToUid);
      break;
    }

    case SWOP_USER_SUCCESS: {
      setSwitchingToFamilyMember(draft, undefined);
      draft.view.showSwitchingMemberFaliure = false;
      break;
    }

    case SWOP_USER_FAILURE: {
      revertSwitchingToFamilyMember(draft, action.payload.currentLoggedInUid);
      break;
    }

    case INVITE_FAMILY_MEMBER_REQUEST: {
      draft.view.isInviteMemberButtonLoading = true;
      break;
    }

    case INVITE_FAMILY_MEMBER_SUCCESS: {
      draft.view.isInviteMemberButtonLoading = false;
      break;
    }

    case INVITE_FAMILY_MEMBER_FAILURE: {
      draft.view.isInviteMemberButtonLoading = false;
      draft.view.inviteFamilyMemberError = action.payload.error;
      break;
    }

    case LOAD_INVITATION_CODE_SIGNUP_SCREEN: {
      draft.view.userIsContinuingFromSignout = true;
      break;
    }

    case LOAD_INVITE_MEMBER_SCREEN: {
      draft.view.inviteFamilyMemberError = undefined;
    }

    case ACCEPT_FAMILY_PLAN_INVITATION_REQUEST: {
      draft.view.isAcceptingInvitation = true;
      break;
    }

    case ACCEPT_FAMILY_PLAN_INVITATION_SUCCESS: {
      draft.view.isAcceptingInvitation = false;
      draft.view.successfullyJoinedFamilyPlan = action.payload.successfullyJoinedFamilyPlan;
      draft.view.joinedFamilyPlanInformation = action.payload.joinedFamilyPlanInformation;
      break;
    }

    case CLOSE_INVITATION_CODE_WELCOME_SCREEN: {
      draft.view.successfullyJoinedFamilyPlan = undefined;
      draft.view.joinedFamilyPlanInformation = '';
      draft.view.userIsContinuingFromSignout = false;
      break;
    }

    case ACCEPT_FAMILY_PLAN_INVITATION_FAILURE: {
      draft.view.successfullyJoinedFamilyPlan = false;
      draft.view.joinedFamilyPlanInformation = action.payload.error;
      draft.view.isAcceptingInvitation = false;
      break;
    }

    case UPDATE_USER_PROFILE_COLOR_SUCCESS: {
      const { userId , profileColor} = action.payload;
      draft.view.isProfileColorChangeLoading = false;
      draft.data.familyMembers = draft.data.familyMembers.map((member) => {
        if (member.uid === userId) {
          member.familyPlanSettings = {
            ...member.familyPlanSettings,
            profileColor,
          }
          return member;
        }
        return member;
      });
      break;
    }

    case UPDATE_USER_PROFILE_PIN_SUCCESS: {
      const { userId , pin} = action.payload;
      draft.view.isProfilePinChangeLoading = false;
      draft.data.familyMembers = draft.data.familyMembers.map((member) => {
        if (member.uid === userId) {
          member.familyPlanSettings = {
            ...member.familyPlanSettings,
            pin,
          }
          return member;
        }
        return member;
      });
      break;
    }

    case CANCEL_PENDING_FAMILY_PLAN_INVITATION_REQUEST: {
      const pendingInvite = action.payload.pendingInvite;
      draft.view.cancelPendingInviteError = undefined;

      draft.data.pendingInvites.map(pi => {
        if (pi.code === pendingInvite.code) {
          pi.isCancellingInvite = true;
        }
      });
      break;
    }

    case CANCEL_PENDING_FAMILY_PLAN_INVITATION_SUCCESS: {
      const cancelledInvitationCode = action.payload.invitationCode;
      const invitationCodeIndex = draft.data.pendingInvites.findIndex(x => x.code === cancelledInvitationCode);

      draft.data.pendingInvites.splice(invitationCodeIndex, 1);
      break;
    }

    case CANCEL_PENDING_FAMILY_PLAN_INVITATION_FAILURE: {
      const pendingInvite = action.payload.pendingInvite;
      const error = action.payload.error;

      draft.view.cancelPendingInviteError = error;
      draft.data.pendingInvites.map(pi => {
        if (pi.code === pendingInvite.code) {
          pi.isCancellingInvite = false;
        }
      });

      break;
    }

    case UPDATE_USER_PROFILE_PIN_REQUEST: {
      draft.view.isProfilePinChangeLoading = true;
      break;
    }

    case UPDATE_USER_PROFILE_PIN_FAILURE: {
      draft.view.isProfilePinChangeLoading = false;
      break;
    }

    case UPDATE_USER_PROFILE_COLOR_REQUEST: {
      draft.view.isProfileColorChangeLoading = true;
      break;
    }

    case UPDATE_USER_PROFILE_COLOR_FAILURE: {
      draft.view.isProfileColorChangeLoading = false;
      break;
    }

    case REMOVE_FAMILY_PLAN_MEMBER_REQUEST: {
      draft.view.isLeavingFamilyPlan = true;
      draft.view.removeFamilyPlanMemberError = undefined;
      break;
    }

    case REMOVE_FAMILY_PLAN_MEMBER_SUCCESS: {
      const userIdToRemove = action.payload.userIdToRemove;
      const familyMemberToRemove = draft.data.familyMembers.findIndex(x => x.uid === userIdToRemove);

      draft.data.familyMembers.splice(familyMemberToRemove, 1);
      draft.view.isLeavingFamilyPlan = false;
      draft.view.removeFamilyPlanMemberError = undefined;
      break;
    }

    case REMOVE_FAMILY_PLAN_MEMBER_FAILURE: {
      draft.view.isLeavingFamilyPlan = false;
      draft.view.removeFamilyPlanMemberError = action.payload.error;
      break;
    }

    case LOAD_CONFIRM_REMOVE_SCREEN: {
      draft.view.isLeavingFamilyPlan = false;
      draft.view.removeFamilyPlanMemberError = undefined;
      break;
    }

    case MANAGE_MEMBERS: {
      draft.view.isManagingMembers = true;
      break;
    }

    case MANAGE_MEMBERS_DONE: {
      draft.view.isManagingMembers = false;
      break;
    }

    case PIN_ATTEMPT_CHANGED: {
      const { pin } = action.payload;
      draft.view.pinAttempt = pin;
      if (pin.length !== 5) {
        draft.view.showSwitchingMemberFaliure = false;
      }
      break;
    }

    default:
      break;
  }
  return draft;
});

function setSwitchingToFamilyMember(draft: IFamilyMembersState, uid: string | undefined) {
  draft.data.familyMembers.map((member) => {
    if (member.uid === uid) {
      member.isSwoppingToUser = member.uid === uid;
      member.isCurrentLoggedInUser = member.uid === uid;
    } else {
      member.isCurrentLoggedInUser = false;
    }
  });
  draft.data.pendingInvites = []
}

function revertSwitchingToFamilyMember(draft: IFamilyMembersState, currentLoggedInUserId: string) {
  draft.view.showSwitchingMemberFaliure = true;
  draft.data.familyMembers.map((member) => {
    member.isCurrentLoggedInUser = member.uid === currentLoggedInUserId;
    member.isSwoppingToUser = false;
  });
}

function sortFamilyMembers(familyMembers: ISwitchMemberUserProfile[]) {
  const familyPlanAdmin = familyMembers.filter((member) => member.isFamilyPlanAdmin);
  const familyPlanMembers = familyMembers.filter((member) => !member.isFamilyPlanAdmin);
  familyPlanMembers.sort((a, b) => a.firstname.localeCompare(b.firstname));

  return [...familyPlanAdmin, ...familyPlanMembers];
}

